import { useEffect, useRef } from "react";

// Gedeeld dialooggedrag voor Modal, Drawer, BottomSheet (en daarmee ConfirmDialog):
//
//   - startfocus   bij openen gaat de focus naar `initialFocusRef`, anders naar het eerste
//                  element met [data-autofocus], anders naar het eerste focusbare element
//                  in de inhoud ([data-dialog-body]), anders naar het eerste focusbare
//                  element in de dialoog, anders naar de dialoog zelf (tabIndex -1).
//   - focus-trap   Tab/Shift+Tab blijven binnen de dialoog.
//   - Esc          roept `onEscape` aan, maar ALLEEN voor de bovenste open dialoog: een
//                  ConfirmDialog boven een Drawer sluit zo enkel zichzelf.
//   - focus terug  bij sluiten keert de focus terug naar het element dat focus had toen de
//                  dialoog openging (de trigger), als dat nog in het document staat.
//
// De trap luistert NATIEF op de container en niet via een React-onKeyDown: React-events
// bubbelen door portals heen in de componentboom, waardoor een geneste dialoog (ook een
// portal) anders de Tab-toetsen van zijn ouder zou triggeren.

const FOCUSABLE = [
  "a[href]",
  "area[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "iframe",
  "[contenteditable='true']",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function focusables(root) {
  if (!root) return [];
  return [...root.querySelectorAll(FOCUSABLE)].filter(
    (el) => !el.hasAttribute("hidden") && el.getAttribute("aria-hidden") !== "true"
  );
}

// Stapel van open dialogen (bovenste = laatste). Module-niveau: één stapel voor de app.
const stack = [];

export function isTopDialog(token) {
  return stack[stack.length - 1] === token;
}

// Aantal dialogen dat op dit moment open staat (ModalFrame gebruikt dit bij het openen om
// te beslissen of een dialoog boven een andere komt: laag --z-dialog).
export function openDialogCount() {
  return stack.length;
}

export default function useDialogFocus({ open, containerRef, onEscape, initialFocusRef }) {
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    if (!open) return undefined;
    const token = {};
    stack.push(token);
    const trigger = typeof document !== "undefined" ? document.activeElement : null;
    const container = containerRef.current;

    // Startfocus.
    const pick = () => {
      if (initialFocusRef?.current) return initialFocusRef.current;
      if (!container) return null;
      const auto = container.querySelector("[data-autofocus]");
      if (auto) return auto;
      const body = container.querySelector("[data-dialog-body]");
      const inBody = focusables(body)[0];
      if (inBody) return inBody;
      return focusables(container)[0] || container;
    };
    const target = pick();
    if (target && typeof target.focus === "function") target.focus();

    const onKeyDown = (e) => {
      if (e.key !== "Tab" || !container) return;
      const items = focusables(container);
      if (items.length === 0) {
        e.preventDefault();
        container.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === container)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    const onDocKey = (e) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      if (!isTopDialog(token)) return;
      e.preventDefault();
      onEscapeRef.current?.();
    };

    container?.addEventListener("keydown", onKeyDown);
    document.addEventListener("keydown", onDocKey);

    return () => {
      container?.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("keydown", onDocKey);
      const i = stack.indexOf(token);
      if (i >= 0) stack.splice(i, 1);
      if (trigger && typeof trigger.focus === "function" && document.contains(trigger)) {
        trigger.focus();
      }
    };
    // initialFocusRef/containerRef zijn refs: enkel `open` bepaalt de levenscyclus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}
